import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  FaHome,
  FaBook,
  FaBriefcase,
  FaSignInAlt,
  FaEnvelope,
  FaRegUser,
  FaUserPlus,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaUser,
} from "react-icons/fa";
import { useAuth } from "../components/Authentication/AuthContext";
import { useToastHelpers } from "../components/Toasts/ToastUtils";
import ThemeSwitcher from "./Theme/ThemeSwitcher";
import { useTheme } from "./Theme/ThemeContext";
import NotificationBell from "./notifications/NotificationBell";

export default function Header() {
  const { loggedInUser, isAuthenticated, logout } = useAuth();
  const { currentTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { successToast } = useToastHelpers();

  // Track scroll for header shadow effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const performLogout = () => {
    logout();
    successToast("Logged out successfully!");
    window.location.href = "/login";
  };

  // Close nested menus when main mobile menu closes
  useEffect(() => {
    if (!menuOpen) setJobsOpen(false);
  }, [menuOpen]);

  const navItems = [
    { to: "/", label: "Home", icon: <FaHome className="w-4 h-4" />, end: true },
    { to: "/courses", label: "Courses", icon: <FaBook className="w-4 h-4" /> },
    { to: "/jobs", label: "Jobs", icon: <FaBriefcase className="w-4 h-4" /> },
    { to: "/internships", label: "Internships", icon: <FaBook className="w-4 h-4" /> },
    { to: "/referrals", label: "Referrals", icon: <FaEnvelope className="w-4 h-4" /> },
  ];

  const isAdmin = String(loggedInUser?.role || "").toLowerCase() === "admin";
  if (isAdmin) {
    navItems.push({ to: "/admin", label: "Admin", icon: <FaRegUser className="w-4 h-4" /> });
  }

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "glass-strong shadow-glass-lg" 
          : "glass-subtle"
      }`}
    >
      <div className="ca-container">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className={`w-10 h-10 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-theme-glow group-hover:scale-105 transition-all duration-300`}>
              <span className="text-white font-bold text-lg">C</span>
            </div>
            <span className={`text-xl font-display font-bold bg-gradient-to-r ${currentTheme.colors.gradientText} bg-clip-text text-transparent`}>
              CodeArena
            </span>
          </Link>

          {/* Mobile right side: Notification + Hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="p-2.5 rounded-xl glass hover:bg-white/50 dark:hover:bg-white/10 text-neutral-600 dark:text-neutral-300 transition-all duration-200"
            >
              {menuOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
            </button>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ to, label, icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive 
                      ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md ${currentTheme.colors.glow}` 
                      : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white"
                  }`
                }
              >
                {icon}
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Right side (Desktop) */}
          <div className="hidden md:flex items-center gap-3">
            {/* Theme Switcher */}
            <ThemeSwitcher />
            
            {/* Notification Bell */}
            <NotificationBell />

            {!isAuthenticated ? (
              <>
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5 transition-all duration-200"
                >
                  <FaSignInAlt className="w-4 h-4" />
                  <span>Login</span>
                </Link>
                <Link
                  to="/signup"
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md ${currentTheme.colors.glow} hover:shadow-lg hover:scale-105 transition-all duration-200 btn-shine`}
                >
                  <FaUserPlus className="w-4 h-4" />
                  <span>Sign Up</span>
                </Link>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5 transition-all duration-200"
                  title="My Dashboard"
                >
                  <i className="ri-dashboard-line text-lg text-theme-primary"></i>
                </Link>
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5 transition-all duration-200"
                >
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${currentTheme.colors.gradient} flex items-center justify-center text-white font-bold text-xs shadow-md`}>
                    {loggedInUser?.firstName && loggedInUser?.lastName
                      ? loggedInUser.firstName[0].toUpperCase() +
                        loggedInUser.lastName[0].toUpperCase()
                      : "U"}
                  </div>
                  <span className="hidden lg:inline">{loggedInUser?.firstName || "Profile"}</span>
                </Link>
                <button
                  onClick={performLogout}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50/50 dark:hover:bg-red-900/20 transition-all duration-200"
                >
                  <FaSignOutAlt className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Dropdown */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            menuOpen ? "max-h-[80vh] opacity-100 pb-4" : "max-h-0 opacity-0"
          }`}
        >
          <div className="glass-strong rounded-2xl shadow-glass-lg p-3 mt-2 space-y-1 animate-slide-down">
            {/* Mobile primary nav (short list) */}
            {navItems
              .filter(({ to }) => to !== "/internships" && to !== "/referrals")
              .filter(({ to }) => to !== "/jobs")
              .map(({ to, label, icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive 
                        ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md` 
                        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5"
                    }`
                  }
                >
                  {icon}
                  {label}
                </NavLink>
              ))}

            {/* Jobs collapsible group (Jobs / Internships / Referrals) */}
            <button
              type="button"
              onClick={() => setJobsOpen((prev) => !prev)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium w-full text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5 transition-all duration-200"
            >
              <FaBriefcase className="w-4 h-4" />
              <span>Jobs</span>
              <span className={`ml-auto text-xs text-neutral-400 transition-transform duration-200 ${jobsOpen ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ${
                jobsOpen ? "max-h-64 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="pl-4 pr-2 pb-2 space-y-1">
                <NavLink
                  to="/jobs"
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive 
                        ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md` 
                        : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100/50 dark:hover:bg-white/5"
                    }`
                  }
                >
                  <FaBriefcase className="w-4 h-4" />
                  All Jobs
                </NavLink>

                <NavLink
                  to="/internships"
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive 
                        ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md` 
                        : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100/50 dark:hover:bg-white/5"
                    }`
                  }
                >
                  <FaBook className="w-4 h-4" />
                  Internships
                </NavLink>

                <NavLink
                  to="/referrals"
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive 
                        ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md` 
                        : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100/50 dark:hover:bg-white/5"
                    }`
                  }
                >
                  <FaEnvelope className="w-4 h-4" />
                  Referrals
                </NavLink>
              </div>
            </div>
            
            <div className="border-t border-neutral-200/50 dark:border-white/10 my-2 pt-2">
              {/* Theme Switcher for Mobile */}
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Theme</span>
                <ThemeSwitcher />
              </div>
              
              {!isAuthenticated ? (
                <>
                  <Link
                    to="/login"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5 transition-all duration-200"
                    onClick={() => setMenuOpen(false)}
                  >
                    <FaSignInAlt className="w-4 h-4" />
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md transition-all duration-200`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <FaUserPlus className="w-4 h-4" />
                    Sign Up
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5 transition-all duration-200"
                    onClick={() => setMenuOpen(false)}
                  >
                    <i className="ri-dashboard-line text-lg text-theme-primary"></i>
                    Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/5 transition-all duration-200"
                    onClick={() => setMenuOpen(false)}
                  >
                    <FaUser className="w-4 h-4" />
                    Profile
                  </Link>
                  <button
                    onClick={() => {
                      performLogout();
                      setMenuOpen(false);
                    }}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50/50 dark:hover:bg-red-900/20 transition-all duration-200"
                  >
                    <FaSignOutAlt className="w-4 h-4" />
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
